import { camelize } from "camelscore";
import GrailedAPIError, {
  fromResponse as grailedErrorFromResponse,
  fromReason as grailedErrorFromReason,
} from "../Error";
import GrailedAPIFetchError from "../FetchError";
import type { Options } from "./Options";
import { fromOptions as headersFromOptions } from "./Headers";
import {
  fromOptions as timeoutFromOptions,
  DEFAULT_TIMEOUT_MS,
} from "./Timeout";
import RequestWatcher from "./RequestWatcher";

const goatenvEnvoyEgress = process.env.GOATENV_ENVOY_EGRESS;

const baseUrl = goatenvEnvoyEgress || "";

function parseText<T>(text: string): T {
  // We sometimes return 200's with empty bodies and set it to JSON, which isn't valid (should be
  // 204 / not JSON). In the meantime, we skip over empty bodies here
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!text) return {} as T;
  return camelize(JSON.parse(text));
}

const throwError = (error: GrailedAPIError): never => {
  throw error;
};

// Bounds the walk below so a cyclic or absurdly deep cause chain cannot run away.
const MAX_CAUSE_DEPTH = 3;

// Node's fetch reports every network failure as a generic "fetch failed" TypeError and puts the
// socket error (ECONNREFUSED, ENOTFOUND, ...) on its cause, so the chain is what names the failure.
const failureMessage = (reason: unknown): string => {
  const messages: Array<string> = [];
  let current: unknown = reason;
  while (current instanceof Error && messages.length < MAX_CAUSE_DEPTH) {
    messages.push(current.message);
    current = "cause" in current ? current.cause : undefined;
  }

  return messages.length > 0 ? messages.join(": ") : String(reason);
};

// Response.url drops a default port, so without this the same endpoint would appear as
// `envoy-egress:80/...` in fetch failures and `envoy-egress/...` in upstream errors. Relative and
// unparseable urls are reported as given.
const normalizeUrl = (url: string): string => {
  try {
    return new URL(url).href;
  } catch {
    return url;
  }
};

// `signal` belongs to the timeout controller, so `signal.aborted` says whether our timer fired. A
// browser cancelling an in-flight fetch (navigation, Stop) also rejects with an AbortError but
// leaves our signal untouched, so it is reported as a network failure rather than a timeout.
const fetchFailure = (
  method: string,
  url: string,
  timeoutMs: number,
  signal: AbortSignal,
  reason: unknown,
): GrailedAPIFetchError => {
  const fullUrl = normalizeUrl(url);
  const requestLine = `${method.toUpperCase()} ${fullUrl}`;
  if (signal.aborted) {
    return new GrailedAPIFetchError(
      `${requestLine} timed out after ${timeoutMs}ms`,
      { kind: "timeout", url: fullUrl, cause: reason },
    );
  }

  return new GrailedAPIFetchError(
    `${requestLine} failed without a response: ${failureMessage(reason)}`,
    { kind: "network", url: fullUrl, cause: reason },
  );
};

// A failure reading the body is a network failure (connection dropped, or the page navigated away
// mid-download), the same as fetch() rejecting before headers, so it propagates untouched. Only an
// unparseable body is the API's fault.
async function parseResponse<T>(
  rsp: Response,
  options: RequestInit,
): Promise<T> {
  const text = await rsp.text();
  try {
    return parseText<T>(text);
  } catch (reason) {
    return throwError(grailedErrorFromReason(rsp, options)(reason));
  }
}

export const requestWatcher = new RequestWatcher();

const request = async <T>(
  url: string,
  opts: Options | null | undefined,
): Promise<T> => {
  requestWatcher.addRequest(url, opts?.body);

  const { body, method = "GET", timeout = DEFAULT_TIMEOUT_MS } = opts || {};
  const headers = new Headers(headersFromOptions(opts));
  if (goatenvEnvoyEgress) headers.set("x-goat-app", "grailed-rails");
  // Setting credentials to "include" in development will allow cookies to be included in
  // cross-origin requests so we can login from storefront locally
  // See More: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
  const credentials =
    process.env.NODE_ENV === "development" ? "include" : "same-origin";
  const [timeoutId, signal] = timeoutFromOptions(opts);
  const options: RequestInit = {
    body,
    method,
    headers,
    credentials,
    signal,
  };

  const handleResponse = (response: Response): Promise<T> | Promise<never> => {
    clearTimeout(timeoutId);
    if (response.ok) return parseResponse<T>(response, options);
    return grailedErrorFromResponse(options)(response).then(throwError);
  };

  // NOTE: only fetch's own rejections (timeout or network failure) are wrapped as
  // GrailedAPIFetchErrors; GrailedAPIErrors thrown by handleResponse pass through untouched.
  let response: Response;
  try {
    response = await fetch(baseUrl + url, options);
  } catch (reason) {
    clearTimeout(timeoutId);
    throw fetchFailure(method, baseUrl + url, timeout, signal, reason);
  }

  return handleResponse(response);
};

export default request;
